/**
 * DocumentsTab Component
 *
 * Unified documents view with client filtering.
 * Includes "Pending Classification" as a filter option.
 */

import { useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useDocuments } from '@/hooks/useDocuments';
import { DocumentsTable } from './DocumentsTable';
import { PendingClassifications } from './PendingClassifications';
import type {
  Client,
  Document,
  PendingClassification,
} from '@/types/management';

interface DocumentsTabProps {
  clients: Client[];
  pending: PendingClassification[];
  isLoadingPending: boolean;
  pendingHasFetched: boolean;
  pendingError: string | null;
  onResolve: (id: string, clientId: string) => Promise<void>;
  onDismiss: (id: string) => Promise<void>;
  onDocumentUnassigned?: () => void;
  clientSlug?: string;
  /** When true, disables all mutation actions */
  readOnly?: boolean;
}

/**
 * Sanitize client name to URL-safe slug
 */
function sanitizeClientSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

/**
 * Find client name from slug
 */
function findClientNameBySlug(
  clients: Client[],
  slug: string,
): string | undefined {
  const client = clients.find(
    (c) => sanitizeClientSlug(c.name) === slug.toLowerCase(),
  );
  return client?.name;
}

export function DocumentsTab({
  clients,
  pending,
  isLoadingPending,
  pendingHasFetched,
  pendingError,
  onResolve,
  onDismiss,
  onDocumentUnassigned,
  clientSlug: propClientSlug,
  readOnly = false,
}: DocumentsTabProps) {
  const params = useParams<{ clientSlug?: string }>();
  const navigate = useNavigate();

  // Use prop if provided, otherwise fall back to URL param
  const clientSlug = propClientSlug ?? params.clientSlug;

  // Determine effective filter from URL
  const effectiveFilter = clientSlug || 'pending';

  // Fetch documents for client (skip if pending)
  const {
    documents,
    isLoading: isLoadingDocuments,
    error: documentsError,
    hasFetched: documentsHasFetched,
    unassign,
  } = useDocuments(effectiveFilter !== 'pending' ? effectiveFilter : null);

  // Handle filter change
  const handleFilterChange = useCallback(
    (value: string) => {
      if (value === 'pending') {
        navigate('/management/documents');
      } else {
        navigate(`/management/documents/${value}`);
      }
    },
    [navigate],
  );

  // Handle document unassignment
  const handleUnassign = useCallback(
    async (doc: Document) => {
      const result = await unassign(doc.s3Key);
      if (!result.alreadyUnassigned) {
        onDocumentUnassigned?.();
      }
    },
    [unassign, onDocumentUnassigned],
  );

  // Get display name for current selection
  const currentClientName = clientSlug
    ? findClientNameBySlug(clients, clientSlug) || clientSlug
    : undefined;

  return (
    <div className="space-y-4">
      {/* Filter Dropdown */}
      <div className="flex items-center gap-4">
        <Select value={effectiveFilter} onValueChange={handleFilterChange}>
          <SelectTrigger className="w-[280px]">
            <SelectValue placeholder="Select filter..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="pending">
              Pending Classification
              {pending.length > 0 && ` (${pending.length})`}
            </SelectItem>
            {clients.length > 0 && (
              <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">
                Clients
              </div>
            )}
            {clients.map((client) => (
              <SelectItem
                key={client.id}
                value={sanitizeClientSlug(client.name)}
              >
                {client.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Content */}
      {effectiveFilter === 'pending' ? (
        <PendingClassifications
          items={pending}
          clients={clients}
          isLoading={isLoadingPending}
          hasFetched={pendingHasFetched}
          error={pendingError}
          onResolve={onResolve}
          onDismiss={onDismiss}
          readOnly={readOnly}
        />
      ) : (
        <DocumentsTable
          documents={documents}
          isLoading={isLoadingDocuments}
          hasFetched={documentsHasFetched}
          error={documentsError}
          clientName={currentClientName || effectiveFilter}
          onUnassign={handleUnassign}
          readOnly={readOnly}
        />
      )}
    </div>
  );
}
